import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import {
  Box,
  Grid,
  MenuItem,
  Select,
  Typography,
  FormControl,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
} from "@mui/material";
import ChartTable from "../../../components/chart-table";
import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import { HOSTNAME, REST_URLS } from "../../../utils/endpoints";
import { MONTHS } from "./config";
import { getFinancialYears } from "../../../core/india-map/utils";

const TrainingDashboard = () => {
  const [financialYear, setFinancialYear] = useState("2025-2026");
  const [type, setType] = useState("quarterly");
  const [data, setData] = useState(null);

  const handleYearChange = (e) => setFinancialYear(e.target.value);
  const handleTypeChange = (e) => setType(e.target.value);
  const fetchTrainingData = (financialYear, type) => {
    const params = {
      financialYear,
    };
    if (type === "quarterly") {
      params["type"] = type;
    }
    invokeApi(
      HTTP_METHODS.GET,
      `${HOSTNAME}${REST_URLS.TRAINING_TOUCH_POINTS}/quarterly-report`,
      null,
      params
    )
      .then((res) => {
        if (!res.message) {
          setData(res);
        } else {
          setData(null);
        }
      })
      .catch((err) => console.error(err));
  };
  useEffect(() => {
    fetchTrainingData(financialYear, type);
  }, [financialYear, type]);

  const buildChartData = () => {
    const isMonthly = type === "monthly";

    const categories = isMonthly
      ? Object.entries(data.quarters).flatMap(([_, q]) =>
          q.months.map(
            (m) => MONTHS?.find((mon) => mon.value === m?.month)?.label
          )
        )
      : Object.keys(data?.quarters);

    const targetManHours = isMonthly
      ? Object.entries(data.quarters).flatMap(([_, q]) =>
          q.months.map((m) => m.targetManHours)
        )
      : Object.entries(data.quarters).map(([_, q]) => q.totalTargetManHours);

    const actualManHours = isMonthly
      ? Object.entries(data.quarters).flatMap(([_, q]) =>
          q.months.map((m) => m.actualManHours)
        )
      : Object.entries(data.quarters).map(([_, q]) => q.totalActualManHours);

    const targetTouchPoints = isMonthly
      ? Object.entries(data.quarters).flatMap(([_, q]) =>
          q.months.map((m) => m.targetTouchPoints)
        )
      : Object.entries(data.quarters).map(([_, q]) => q.totalTargetTouchPoints);

    const actualTouchPoints = isMonthly
      ? Object.entries(data.quarters).flatMap(([_, q]) =>
          q.months.map((m) => m.actualTouchPoints)
        )
      : Object.entries(data.quarters).map(([_, q]) => q.totalActualTouchPoints);

    // Append yearly total
    categories.push("Yearly Total");
    targetManHours.push(data.yearlyTotals.totalTargetManHours);
    actualManHours.push(data.yearlyTotals.totalActualManHours);
    targetTouchPoints.push(data.yearlyTotals.totalTargetTouchPoints);
    actualTouchPoints.push(data.yearlyTotals.totalActualTouchPoints);

    return {
      categories,
      manHours: [
        { name: "Target", data: targetManHours },
        { name: "Actual", data: actualManHours },
      ],
      touchPoints: [
        { name: "Target", data: targetTouchPoints },
        { name: "Actual", data: actualTouchPoints },
      ],
    };
  };

  const chartData = data ? buildChartData() : {};

  const getMonthName = (monthNum) => {
    const month = MONTHS?.find((mon) => mon.value === monthNum)?.label;
    return month;
  };

  const headers = data
    ? type === "quarterly"
      ? ["", "Q1", "Q2", "Q3", "Q4", `Total FY ${data?.financialYear}`]
      : [
          "",
          ...Object.values(data.quarters).flatMap((q) =>
            q.months.map((m) => getMonthName(m.month))
          ),
          `Total FY ${data.financialYear}`,
        ]
    : [];
  const manHoursRows = data
    ? type === "quarterly"
      ? [
          [
            "Target Man Hours",
            ...Object.values(data.quarters).map((q) => q.totalTargetManHours),
            data.yearlyTotals.totalTargetManHours,
          ],
          [
            "Actual Man Hours",
            ...Object.values(data.quarters).map((q) => q.totalActualManHours),
            data.yearlyTotals.totalActualManHours,
          ],
        ]
      : [
          [
            "Target Man Hours",
            ...Object.values(data.quarters).flatMap((q) =>
              q.months.map((m) => m.targetManHours)
            ),
            data.yearlyTotals.totalTargetManHours,
          ],
          [
            "Actual Man Hours",
            ...Object.values(data.quarters).flatMap((q) =>
              q.months.map((m) => m.actualManHours)
            ),
            data.yearlyTotals.totalActualManHours,
          ],
        ]
    : [];

  const touchPointsRows = data
    ? type === "quarterly"
      ? [
          [
            "Target Touch Points",
            ...Object.values(data.quarters).map(
              (q) => q.totalTargetTouchPoints
            ),
            data.yearlyTotals.totalTargetTouchPoints,
          ],
          [
            "Actual Touch Points",
            ...Object.values(data.quarters).map(
              (q) => q.totalActualTouchPoints
            ),
            data.yearlyTotals.totalActualTouchPoints,
          ],
        ]
      : [
          [
            "Target Touch Points",
            ...Object.values(data.quarters).flatMap((q) =>
              q.months.map((m) => m.targetTouchPoints)
            ),
            data.yearlyTotals.totalTargetTouchPoints,
          ],
          [
            "Actual Touch Points",
            ...Object.values(data.quarters).flatMap((q) =>
              q.months.map((m) => m.actualTouchPoints)
            ),
            data.yearlyTotals.totalActualTouchPoints,
          ],
        ]
    : [];

  return (
    <Box p={4}>
      <Grid container spacing={2} alignItems="center" mb={4}>
        <Grid item xs={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Financial Year</InputLabel>
            <Select
              value={financialYear}
              onChange={handleYearChange}
              label="Financial Year"
            >
              {getFinancialYears()?.map((year) => (
                <MenuItem key={year?.value} value={year?.value}>
                  {year?.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Type</InputLabel>
            <Select value={type} onChange={handleTypeChange} label="Type">
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Card>
        <CardContent>
          <Typography variant="h6">Training - Man hours</Typography>
          {data ? (
            <div>
              <Chart
                type="bar"
                height={300}
                options={{
                  chart: { id: "man-hours" },
                  xaxis: { categories: chartData.categories },
                  colors: ["#008FFB", "#FF4560"],
                  plotOptions: {
                    bar: {
                      columnWidth: "30%", // reduce bar thickness
                    },
                  },
                  dataLabels: {
                    enabled: true, 
                  },
                }}
                series={chartData.manHours}
              />
              <ChartTable headers={headers} rows={manHoursRows} />
            </div>
          ) : (
            <Typography sx={{ textAlign: "center", mt: 5, mb: 5 }}>
              No Data Found
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Touch Points */}
      <Card sx={{ mt: 5 }}>
        <CardContent>
          <Typography variant="h6">Training - Touch Points</Typography>
          {data ? (
            <div>
              <Chart
                type="bar"
                height={300}
                options={{
                  chart: { id: "touch-points" },
                  xaxis: { categories: chartData.categories },
                  colors: ["#00E396", "#775DD0"],
                  plotOptions: {
                    bar: {
                      columnWidth: "30%", // reduce bar thickness
                    },
                  },
                  dataLabels: {
                    enabled: true, 
                  },
                }}
                series={chartData.touchPoints}
              />
              <ChartTable headers={headers} rows={touchPointsRows} />
            </div>
          ) : (
            <Typography sx={{ textAlign: "center", mt: 5, mb: 5 }}>
              No Data Found
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TrainingDashboard;
